import { useEffect, useState } from 'react';
import { Navigate, useParams } from 'react-router';
import { toast } from 'react-toastify';
import isEmpty from 'lodash/isEmpty';
import classNames from 'classnames';
import { subYears } from '@querodelivery/qd-date-helper';
import { CardWithTitle } from '@/_features/motoboys/motoboy-details/components/card-with-title';
import { HeaderButtons } from '@/_features/motoboys/motoboy-details/components/header-buttons';
import { LaunchBank } from '@/_features/motoboys/motoboy-details/components/launch-bank';
import { DisapprovalLabel } from '@/_features/motoboys/motoboy-details/components/disapproval-label';
import { Documents } from '@/_features/motoboys/motoboy-details/components/documents';
import type { PreRegisterInfo } from '@/_features/motoboys/motoboy-list/types';
import { LinkedCard } from '@/_features/motoboys/motoboy-details/components/card-linked';
import styles from './motoboy-details.module.scss';
import type { TUserType } from '@/Interfaces/Data';
import JsonView from '@/Shared/JsonView';
import Balance from '@/Components/Balance/Balance';
import LastDeliveries from '@/features/lastDeliveries/LastDeliveries';
import MotoboyInfo from '@/Components/motoboy/motoboy-info/MotoboyInfo';
import useMotoboy from '@/hooks/motoboy/useMotoboy';
import DedicationInfo from '@/Components/motoboy/dedication-info/DedicationInfo';
import { Extract } from '@/_features/motoboys/extract';
import {
  DELIVERYMAN_NOT_FOUND_TOAST,
  ENUM_STATUS_PRE_REGISTRATION,
} from '@/_features/motoboys/constants';
import { ExtractsListModal } from '@/_features/places/components/extract-list-modal/extract-list-modal';
import { useGetExtracts } from '@/_features/motoboys/extract/api/get-extracts';
import { ENUM_DELIVERYMAN_STATUS } from '@/_constants/enums';
import { MotoboyDetailsSkeleton } from './motoboy-details-skeleton';
import { MotoboyStatus } from '@/features/motoboy/motoboy-status/motoboy-status';

function MotoboyDetails() {
  const USER_TYPE: TUserType = 'MOTOBOY';

  const { motoboyId } = useParams<{ motoboyId: string }>();

  const { errorsMotoboy, loadingMotoboy, motoboy, updateMotoboy } = useMotoboy(
    motoboyId || '',
  );

  const [showMoreExtracts, setShowMoreExtracts] = useState(false);
  const [page, setPage] = useState(1);

  const [params, setParams] = useState({
    startDate: subYears(new Date(), 1).toISOString(),
    endDate: new Date().toISOString(),
  });

  const { data: extractsData, isLoading: isLoadingExtracts } = useGetExtracts(
    {
      deliverymanId: motoboyId || '',
      startDate: params.startDate,
      endDate: params.endDate,
      page,
    },
    {
      enabled: !!motoboyId && motoboy?._id === motoboyId,
    },
  );

  const totalCount = extractsData?.data.total_count;
  const extracts = extractsData?.data.results;

  useEffect(() => {
    window.scrollTo({ top: 0 });
  }, []);

  // TODO: Remove this useEffect after refactoring motoboy requests
  useEffect(() => {
    updateMotoboy(motoboyId || '');
  }, []);

  if (!motoboyId) {
    return <Navigate to="/entregadores" />;
  }

  if (!motoboy && !isEmpty(errorsMotoboy)) {
    toast.warn(DELIVERYMAN_NOT_FOUND_TOAST.message, {
      toastId: DELIVERYMAN_NOT_FOUND_TOAST.toastId,
    });
    return <Navigate to="/entregadores" />;
  }

  const registerStatus =
    (motoboy?.status as ENUM_DELIVERYMAN_STATUS) ===
    ENUM_DELIVERYMAN_STATUS.EXCLUIDO
      ? ENUM_STATUS_PRE_REGISTRATION.DELETED
      : motoboy?.preRegistration?.status;

  const isReprovedOrNewRegister = registerStatus !== 'APPROVED';
  const showDisapprovalLabel =
    isReprovedOrNewRegister &&
    registerStatus &&
    Object.values(ENUM_STATUS_PRE_REGISTRATION).includes(registerStatus);

  const hasPlacesLinked =
    motoboy?.placesLinked && Object.keys(motoboy.placesLinked).length > 0;

  return (
    <div
      className={classNames(
        'container',
        styles.scrolledX,
        showDisapprovalLabel && styles.mt,
      )}
    >
      {loadingMotoboy || !motoboy?._id ? (
        <MotoboyDetailsSkeleton />
      ) : (
        <>
          {showDisapprovalLabel && (
            <DisapprovalLabel disapprovalStatus={registerStatus} />
          )}
          <HeaderButtons />
          <div className={styles.motoboyDetailsTitle}>
            <div className={styles.balanceContainer}>
              <Balance deliverymanId={motoboyId} />
            </div>
          </div>

          <main className={styles.motoboyDetails}>
            <section className={styles.section}>
              <MotoboyStatus />
              <CardWithTitle title="Informações">
                <MotoboyInfo motoboy={motoboy} />
              </CardWithTitle>
              {motoboy.preRegistration
                ? !motoboy.deletedAt && (
                    <CardWithTitle title="Documentos">
                      {/* TODO: remove conversion type after refactoring motoboy requests */}
                      <Documents
                        deliverymanBackRgPictureKey={
                          motoboy.deliverymanBackRgPictureKey || ''
                        }
                        deliverymanRgFrontPictureKey={
                          motoboy.deliverymanFrontRgPictureKey || ''
                        }
                        deliverymanCnhPictureKey={
                          motoboy.deliverymanCnhPictureKey || ''
                        }
                        deliverymanCrlvPictureKey={
                          motoboy.deliverymanCrlvPictureKey || ''
                        }
                        deliverymanPreRegistration={
                          motoboy.preRegistration as unknown as PreRegisterInfo
                        }
                      />
                    </CardWithTitle>
                  )
                : null}
            </section>
            <section className={styles.section}>
              {motoboy.isDedicated && (
                <CardWithTitle title="Dedicação">
                  <DedicationInfo motoboyId={motoboy._id} />
                </CardWithTitle>
              )}
              {hasPlacesLinked ? (
                <CardWithTitle title="Vinculação">
                  <LinkedCard placesLinked={motoboy.placesLinked} />
                </CardWithTitle>
              ) : null}
              {motoboy.isQueroLogApproved ? (
                <CardWithTitle title="Bank">
                  <LaunchBank userType={USER_TYPE} />
                </CardWithTitle>
              ) : null}

              <CardWithTitle title="Entregas">
                <LastDeliveries motoboy={motoboy} />
              </CardWithTitle>

              <CardWithTitle title="Extrato">
                <Extract
                  deliverymanId={motoboy._id}
                  hasMore={() => setShowMoreExtracts(true)}
                />
              </CardWithTitle>
            </section>
          </main>

          <JsonView name="Entregador" source={motoboy} />

          <ExtractsListModal
            open={showMoreExtracts}
            onClose={() => setShowMoreExtracts(false)}
            setParams={setParams}
            params={params}
            setPage={setPage}
            extracts={extracts || []}
            totalCount={totalCount || 0}
            page={page}
            isLoading={isLoadingExtracts}
          />
        </>
      )}
    </div>
  );
}

export { MotoboyDetails };
